import React, {useCallback, useEffect, useState} from 'react';
import {ExternalApiClientId, ExternalApiSecret} from '@src/dtos/customer.dto';
import {dateFormat, WORDS_MONTHS_SHORT_FORMAT} from '@src/utils/datesUtils';
import {Link} from 'react-router-dom';
import {envKnowledgeBaseLink} from '@src/utils/envLinksUtil';
import {EventType} from '@src/dtos/analytics.dto';
import customerService from '@src/services/customer.service';
import {useStores} from '@src/components/store';
import Loader from '@src/components/shared/loader/loader';
import CopyButton from '@src/components/shared/copy-button/copy-button';
import SuccessIcon from '@svg/check-circle_24.svg?react';
import ErrorIcon from '@svg/red_x.svg?react';
import './api-key-tab.scss';

type ApiClientExpirationElementProps = { expirationDate: Date; };
const ApiClientExpirationElement: React.FC<ApiClientExpirationElementProps> = ({ expirationDate }) => (
    <div className="api-client-label">Client secret
        <div className="api-client-id-expiration-date">
            {`Expires: ${dateFormat(new Date(expirationDate), WORDS_MONTHS_SHORT_FORMAT)}`}
        </div>
    </div>
);

const ApiKeyTab: React.FC = () => {
    const { customerStore } = useStores();
    const [apiSecret, setApiSecret] = useState<ExternalApiSecret | null>(null);
    const [isApiSecretGenerating, setIsApiSecretGenerating] = useState<boolean>(false);
    const [apiClientId, setApiClientId] = useState<ExternalApiClientId | undefined>(undefined);
    const [isApiClientIdLoading, setIsApiClientIdLoading] = useState<boolean>(true);
    const [hasError, setHasError] = useState<boolean>(false);

    const generateApiSecret = useCallback(async () => {
        try {
            setIsApiSecretGenerating(true);
            setHasError(false);
            customerStore.sendGeneralEvent(EventType.LinkClicked, {button: 'generateAPISecret', location: 'generateAPISecret'});
            const data = apiClientId ?
                await customerService.resetApiSecret(customerStore.customerId) :
                await customerService.createApiSecret(customerStore.customerId);

            setApiSecret(data);
            setApiClientId({clientId: data.clientId, expirationDate: data.expirationDate});
            customerStore.sendGeneralEvent(EventType.ModalView, {modalName: 'APISecretSuccessfullyCreated'});
        } catch (err) {
            console.error('Failed to Generate API Secret: ', err);
            setHasError(true);
        } finally {
            setIsApiSecretGenerating(false);
        }
    }, [apiClientId, customerStore.customerId]);

    useEffect(() => {
        const getApiClientId = async () => {
            try {
                const data = await customerService.getExternalApiClientId(customerStore.customerId);
                setApiClientId(data);
            } catch (err) {
                console.error('Failed to fetch External API Client ID: ', err);
            } finally {
                setIsApiClientIdLoading(false);
            }
        };
        void getApiClientId();
    },[customerStore.customerId]);

    const getGenerateApiButtonCaption = (isApiSecretGenerating: boolean, apiClientId: ExternalApiClientId | undefined) => {
        if (isApiSecretGenerating) return 'Generating API secret';
        if (apiClientId) return 'Generate new API secret';
        return 'Generate API secret';
    };

    if(isApiClientIdLoading) {
        return <Loader type={'full-screen'}/>;
    }

    return  (
        <div className="api-key-tab">
            {apiClientId ? (
                <>
                    <div className="api-customer-id">CUSTOMER ID: {customerStore.customerId}</div>
                    <div className="api-client-label">Client ID</div>
                    <div className="api-value-container">
                        <div className="api-client-value">{apiClientId.clientId}</div>
                        <CopyButton value={apiClientId.clientId}/>
                    </div>

                    {apiSecret ? (
                        <div className="api-secret-group">
                            <div className="api-secret-created-container">
                                <SuccessIcon/>
                                API secret successfully created
                            </div>
                            <div className="api-copy-desc">Please copy and store it securely now, as it will only be shown once.</div>
                            <ApiClientExpirationElement expirationDate={apiClientId.expirationDate} />
                            <div className="api-value-container">
                                <div className="api-client-value">{apiSecret.secret}</div>
                                <CopyButton value={apiSecret.secret}/>
                            </div>
                        </div>
                    ) : (
                        <div className="api-no-secret-group">
                            <ApiClientExpirationElement expirationDate={apiClientId.expirationDate} />
                        </div>
                    )}
                </>
            ) : (
                <div className="tab-title">
                    <div>Generate API secret to call Dynamic Defense APIs.</div>
                </div>
            )}

            {hasError && (
                <div className={'error-container'}>
                    <div className={'error-icon'}>
                        <ErrorIcon/>
                    </div>
                    <div className={'error-message-container'}>
                        <div className={'error-message'}>API key generation failed. Try again or</div>
                        <Link to={`${envKnowledgeBaseLink}/support`} className="support-link" target="_blank" rel="noreferrer">
                            contact support.
                        </Link>
                    </div>
                </div>
            )}

            <button type="button" className={`custom-btn primary-btn${isApiSecretGenerating ? ' loading' : ''}`}
                    onClick={generateApiSecret} disabled={isApiClientIdLoading || isApiSecretGenerating}>
                    {getGenerateApiButtonCaption(isApiSecretGenerating, apiClientId)}
            </button>

            <Link to={`${envKnowledgeBaseLink}/attdd`} className="knowledge-link" target="_blank" rel="noreferrer">
                See the Knowledge Base article for more information.
            </Link>
        </div>
    );
};

export default ApiKeyTab;